import { useState } from 'react';
import { CardSummary } from '../../types/cards';
import { useDecks, useCreateDeck, useUpdateDeck, useDeleteDeck, useSetActiveDeck } from '../../hooks/useDecks';
import { DeckBuilder } from './DeckBuilder';
import { DeckList } from './DeckList';
import './DeckManager.css';

interface DeckManagerProps {
  cards: CardSummary[];
}

type DeckViewMode = 'list' | 'builder' | 'edit';

export function DeckManager({ cards }: DeckManagerProps) {
  const [viewMode, setViewMode] = useState<DeckViewMode>('list');
  const [editingDeckId, setEditingDeckId] = useState<string | null>(null);
  const [selectedCardIds, setSelectedCardIds] = useState<number[]>([]);
  const [deckName, setDeckName] = useState('');

  const { data: decks, isLoading } = useDecks();
  const createDeck = useCreateDeck();
  const updateDeck = useUpdateDeck();
  const deleteDeck = useDeleteDeck();
  const setActiveDeck = useSetActiveDeck();

  const handleNewDeck = () => {
    setViewMode('builder');
    setEditingDeckId(null);
    setSelectedCardIds([]);
    setDeckName('');
  };

  const handleEditDeck = (deckId: string, name: string, cardIds: number[]) => {
    setViewMode('builder');
    setEditingDeckId(deckId);
    setSelectedCardIds([...cardIds]);
    setDeckName(name);
  };

  const handleSaveDeck = async () => {
    if (!deckName.trim()) {
      alert('덱 이름을 입력해주세요.');
      return;
    }

    try {
      if (editingDeckId) {
        await updateDeck.mutateAsync({
          deckId: editingDeckId,
          updates: {
            name: deckName,
            card_ids: selectedCardIds,
          },
        });
      } else {
        await createDeck.mutateAsync({
          name: deckName,
          card_ids: selectedCardIds,
        });
      }
      setViewMode('list');
      setEditingDeckId(null);
      setSelectedCardIds([]);
      setDeckName('');
    } catch (error) {
      alert('덱 저장에 실패했습니다: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
    }
  };

  const handleCancel = () => {
    setViewMode('list');
    setEditingDeckId(null);
    setSelectedCardIds([]);
    setDeckName('');
  };

  const handleDeleteDeck = async (deckId: string) => {
    if (!window.confirm('정말 이 덱을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteDeck.mutateAsync(deckId);
    } catch (error) {
      alert('덱 삭제에 실패했습니다: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
    }
  };

  const handleSetActiveDeck = async (deckId: string) => {
    try {
      await setActiveDeck.mutateAsync(deckId);
    } catch (error) {
      alert('기본 덱 설정에 실패했습니다: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
    }
  };

  if (viewMode === 'builder') {
    return (
      <div className="deck-manager">
        <div className="deck-manager__header">
          <button className="deck-manager__back-btn" onClick={handleCancel}>
            ← 목록
          </button>
          <h2 className="deck-manager__title">{editingDeckId ? '덱 수정' : '새 덱 만들기'}</h2>
          <button
            className="deck-manager__save-btn"
            onClick={handleSaveDeck}
            disabled={createDeck.isPending || updateDeck.isPending}
          >
            저장
          </button>
        </div>
        <DeckBuilder
          cards={cards}
          selectedCardIds={selectedCardIds}
          onCardSelect={(cardId) => setSelectedCardIds([...selectedCardIds, cardId])}
          onCardRemove={(cardId) => {
            const index = selectedCardIds.indexOf(cardId);
            if (index > -1) {
              const newIds = [...selectedCardIds];
              newIds.splice(index, 1);
              setSelectedCardIds(newIds);
            }
          }}
          onClear={() => setSelectedCardIds([])}
          deckName={deckName}
          onDeckNameChange={setDeckName}
        />
      </div>
    );
  }

  return (
    <div className="deck-manager">
      <div className="deck-manager__header">
        <h2 className="deck-manager__title">덱 관리</h2>
        <button className="deck-manager__new-btn" onClick={handleNewDeck}>
          + 새 덱
        </button>
      </div>
      <DeckList
        decks={decks || []}
        isLoading={isLoading}
        onEdit={handleEditDeck}
        onDelete={handleDeleteDeck}
        onSetActive={handleSetActiveDeck}
      />
    </div>
  );
}

